import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner, BusyLabel } from '../components/Spinner';
import { useParams, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAccounts } from '../context/AccountsContext';
import { isAccountOwner, hasTokenAccess, hasReconnectAccess } from '../permissions';
import { api } from '../api/client';
import type { Account, Model } from '../api/client';
import { Button, Card, CardHeader, CardContent, CardTitle, Modal } from '@heroui/react';
import { FeatherIcon } from '../components/FeatherIcon';
import { ChangeProxyModal } from '../components/Onboarding/ChangeProxyModal';
import { SessionsPanel } from '../components/Onboarding/SessionsPanel';
import { KillSwitchCard } from '../components/KillSwitchCard';
import { canSwitchAi } from '../dashboard/useAccountPermissions';

export function AccountSettingsPage() {
  const { t } = useTranslation('dashboard');
  const { accountId } = useParams<{ accountId: string }>();
  const navigate = useNavigate();
  const { removeAccount, updateAccount } = useAccounts();
  const { getPermissions, loadAccountPermissions, isSuperAdmin } = useAuth();
  const [account, setAccount] = useState<Account | null>(null);
  const [model, setModel] = useState<Model | null>(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState<string | null>(null);
  const [showToken, setShowToken] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');

  const [editProfileName, setEditProfileName] = useState('');
  const [editUsername, setEditUsername] = useState('');
  const [editModelName, setEditModelName] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState('');
  const [changingProxy, setChangingProxy] = useState(false);

  useEffect(() => {
    if (!accountId) return;
    (async () => {
      try {
        setLoading(true);
        const data = await api.getAccount(accountId);
        setAccount(data);
        setEditProfileName(data.profile_name || '');
        setEditUsername(data.username || '');
        if (data.model_id) {
          try {
            const m = await api.getModel(data.model_id);
            setModel(m);
            setEditModelName(m.model_name || '');
          } catch { /* best-effort */ }
        }
        await loadAccountPermissions(accountId);
      } catch {
        setAccount(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [accountId]);

  if (!accountId) return <Navigate to="/dashboard" replace />;

  const perms = getPermissions(accountId);
  // The Kill switch shares the AI toggle's gate (ADR 0042), so settings-permission
  // holders reach this page too; the token card stays behind token access.
  const canEditKillSwitch = isSuperAdmin || canSwitchAi(perms);
  const canAccess = isAccountOwner(perms) || hasTokenAccess(perms) || canEditKillSwitch;

  if (!loading && !canAccess) {
    return <Navigate to={`/account/${accountId}`} replace />;
  }

  const handleRevealToken = async () => {
    try {
      const data = await api.getAccountToken(accountId!);
      setToken(data.token);
      setShowToken(true);
    } catch {
      setToken(null);
    }
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    setProfileSuccess('');
    try {
      const updates: Partial<Account> = {};
      if (editProfileName !== (account?.profile_name || '')) {
        updates.profile_name = editProfileName.trim() || null;
      }
      if (editUsername !== (account?.username || '')) {
        updates.username = editUsername.trim() || null;
      }

      if (Object.keys(updates).length > 0) {
        const updated = await api.updateAccount(accountId!, updates);
        setAccount(updated);
      }

      if (model && editModelName !== model.model_name) {
        const updatedModel = await api.updateModel(model.model_id, editModelName.trim());
        setModel(updatedModel);
      }

      setProfileSuccess(t('accountSettings.changesSaved'));
      setTimeout(() => setProfileSuccess(''), 3000);
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : t('accountSettings.saveChangesFailed'));
    } finally {
      setSavingProfile(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await api.deleteAccount(accountId!);
      removeAccount(accountId!);
      navigate('/dashboard');
    } catch (err: unknown) {
      setDeleting(false);
      alert(err instanceof Error ? err.message : t('accountSettings.deleteFailed'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size={40} label={t('common:loading')} />
      </div>
    );
  }

  return (
    <>
      <div className="mobile-page-shell max-w-3xl mx-auto p-6 space-y-6 min-w-0">
        <div className="flex items-center gap-3 mb-6">
          <Button
            variant="ghost"
            size="sm"
            onPress={() => navigate(`/account/${accountId}`)}
            className="text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
          >
            <FeatherIcon name="arrow-left" size={16} />
            {t('accountSettings.backToAccount')}
          </Button>
          <h1 className="text-2xl font-semibold text-[var(--color-text-primary)]">{t('accountSettings.title')}</h1>
        </div>

        {/* Profile Settings */}
        <Card className="bg-[var(--color-bg-card)] border border-[var(--color-border-default)]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[var(--color-text-primary)]">
              <FeatherIcon name="edit-3" size={18} />
              {t('accountSettings.profileSettings')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-[var(--color-text-secondary)]">{t('accountSettings.profileName')}</label>
              <input
                type="text"
                value={editProfileName}
                onChange={(e) => setEditProfileName(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-[var(--color-bg-input)] border border-[var(--color-border-default)] text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-primary)]"
                placeholder={t('accountSettings.profileNamePlaceholder')}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-[var(--color-text-secondary)]">{t('accountSettings.username')}</label>
              <input
                type="text"
                value={editUsername}
                onChange={(e) => setEditUsername(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-[var(--color-bg-input)] border border-[var(--color-border-default)] text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-primary)]"
                placeholder={t('accountSettings.usernamePlaceholder')}
              />
            </div>
            {model && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-[var(--color-text-secondary)]">{t('accountSettings.modelName')}</label>
                <input
                  type="text"
                  value={editModelName}
                  onChange={(e) => setEditModelName(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-[var(--color-bg-input)] border border-[var(--color-border-default)] text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-primary)]"
                  placeholder={t('accountSettings.modelNamePlaceholder')}
                />
              </div>
            )}
            {profileSuccess && (
              <p className="text-sm text-green-400">{profileSuccess}</p>
            )}
            <Button
              onPress={handleSaveProfile}
              isDisabled={savingProfile}
              className="bg-[var(--color-accent-primary)] text-white"
            >
              <BusyLabel busy={savingProfile} label={t('common:saving')}>{t('accountSettings.saveChanges')}</BusyLabel>
            </Button>
          </CardContent>
        </Card>

        {/* Token */}
        {hasTokenAccess(perms) && (
        <Card className="bg-[var(--color-bg-card)] border border-[var(--color-border-default)]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[var(--color-text-primary)]">
              <FeatherIcon name="key" size={18} />
              {t('accountSettings.accountToken')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {showToken && token ? (
              <div className="p-3 bg-[var(--color-bg-input)] rounded-lg break-all font-mono text-sm text-[var(--color-text-primary)]">
                {token}
              </div>
            ) : (
              <Button
                variant="outline"
                onPress={handleRevealToken}
                className="text-[var(--color-text-secondary)] border-[var(--color-border-default)]"
              >
                <FeatherIcon name="eye" size={16} />
                {t('accountSettings.revealToken')}
              </Button>
            )}
          </CardContent>
        </Card>
        )}

        {/* Connection — re-login / change proxy through the streamed login agent */}
        {hasReconnectAccess(perms) && (
          <Card className="bg-[var(--color-bg-card)] border border-[var(--color-border-default)]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[var(--color-text-primary)]">
                <FeatherIcon name="wifi" size={18} />
                {t('accountSettings.connection', 'Connection')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-[var(--color-text-secondary)] text-sm">
                {t(
                  'accountSettings.changeProxyHint',
                  'Change the proxy this account uses. OnlyFans binds the session to its IP, so this starts a fresh login through the new proxy.',
                )}
              </p>
              <Button
                variant="outline"
                onPress={() => setChangingProxy(true)}
                className="text-[var(--color-text-secondary)] border-[var(--color-border-default)]"
              >
                <FeatherIcon name="refresh-cw" size={16} />
                {t('accountSettings.changeProxy', 'Change proxy & re-login')}
              </Button>

              <div className="pt-4 border-t border-[var(--color-border-default)]">
                {accountId && <SessionsPanel accountId={accountId} />}
              </div>
            </CardContent>
          </Card>
        )}

        {changingProxy && accountId && (
          <ChangeProxyModal accountId={accountId} platform={account?.platform} onClose={() => setChangingProxy(false)} />
        )}

        {account && (
          <KillSwitchCard
            account={account}
            canEdit={canEditKillSwitch}
            setKillSwitch={(id, on, reason) => api.setKillSwitch(id, on, reason)}
            onChange={(updated) => {
              setAccount(updated);
              updateAccount(updated.account_id, updated);
            }}
          />
        )}

        {isAccountOwner(perms) && (
          <Card className="bg-[var(--color-bg-card)] border border-red-500/30">
            <CardHeader>
              <CardTitle className="text-red-400 flex items-center gap-2">
                <FeatherIcon name="trash-2" size={18} />
                {t('accountSettings.dangerZone')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-[var(--color-text-secondary)] text-sm">
                {t('accountSettings.deleteWarning')}
              </p>
              <Button
                variant="danger"
                onPress={() => setDeleteDialogOpen(true)}
                isDisabled={deleting}
              >
                <FeatherIcon name="trash-2" size={16} />
                <BusyLabel busy={deleting} label={t('accountSettings.deleting')}>{t('accountSettings.deleteAccount')}</BusyLabel>
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
      <Modal>
        <Modal.Backdrop
          isOpen={deleteDialogOpen}
          onOpenChange={(open) => {
            if (!open && !deleting) {
              setDeleteDialogOpen(false);
              setDeleteConfirmation('');
            }
          }}
          className="bg-black/70"
        >
          <Modal.Container placement="center" className="items-center p-4">
            <Modal.Dialog className="w-full max-w-md bg-[var(--color-bg-card)] border border-red-500/30 text-[var(--color-text-primary)] shadow-2xl">
              <Modal.Header className="border-b border-[var(--color-border-default)]">
                <Modal.CloseTrigger isDisabled={deleting} />
                <Modal.Heading className="text-red-400">
                  {t('accountSettings.deleteConfirmTitle', { defaultValue: 'Delete account' })}
                </Modal.Heading>
              </Modal.Header>
              <Modal.Body className="p-5 space-y-4">
                <p className="text-sm text-[var(--color-text-secondary)]">
                  {t('accountSettings.deleteConfirmBody', { defaultValue: 'This permanently deletes the account and all related data.' })}
                </p>
                <label className="block text-xs font-semibold uppercase tracking-wide text-[var(--color-text-tertiary)]">
                  {t('accountSettings.deleteConfirmInstruction', { defaultValue: 'Type DELETE to confirm' })}
                  <input
                    autoFocus
                    value={deleteConfirmation}
                    onChange={(event) => setDeleteConfirmation(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' && deleteConfirmation === 'DELETE') void handleDeleteAccount();
                    }}
                    className="mt-2 w-full rounded-lg border border-[var(--color-border-default)] bg-[var(--color-bg-input)] px-3 py-2 text-sm normal-case tracking-normal outline-none focus:border-red-400"
                    disabled={deleting}
                  />
                </label>
              </Modal.Body>
              <Modal.Footer className="border-t border-[var(--color-border-default)] flex justify-end gap-2 p-4">
                <Button variant="ghost" onPress={() => setDeleteDialogOpen(false)} isDisabled={deleting}>
                  {t('common:cancel')}
                </Button>
                <Button
                  variant="danger"
                  onPress={() => void handleDeleteAccount()}
                  isDisabled={deleting || deleteConfirmation !== 'DELETE'}
                >
                  <BusyLabel busy={deleting} label={t('accountSettings.deleting')}>{t('accountSettings.deleteAccount')}</BusyLabel>
                </Button>
              </Modal.Footer>
            </Modal.Dialog>
          </Modal.Container>
        </Modal.Backdrop>
      </Modal>
    </>
  );
}
